import { hashKey, useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useAppPerformanceContext } from 'src/contexts/AppPerformanceContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { PricingCalculationCacheTimeInMs } from 'src/utils/constants/constants';
import {
  BulkEditListingClient,
  Event,
  Feature,
  Listing,
  ListingPriceCalculationsInEventInput,
} from 'src/WebApiController';

import { useDebouncedValueSafe } from '../useDebouncedValueSafe';
import { useUserHasFeature } from '../useUserHasFeature';

export const useListingGroupAutoPricePreview = (
  event: Event,
  listings: Listing[],
  rawPricingSettingsUpdates: ListingPriceCalculationsInEventInput | undefined,
  enabled = true
) => {
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const hasVeloCBFeature = useUserHasFeature(
    Feature.AutoPricingImprovedPricePreivewForVeloCB
  );
  const hasBulkPriceCalcCancellationFix = useUserHasFeature(
    Feature.BulkPriceCalcRequestCancellation
  );

  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const { trackEndpoint } = useAppPerformanceContext();

  // ListingIds is gated behind `enabled` so that when the group switches and
  // settings are not yet ready (enabled=false), listingIds is cleared to []
  // which keeps shouldQuery false. When enabled, listingIds is updated to the
  // latest value. listingIdsKey (a string primitive) is used instead of the
  // listings array so a cached-settings group switch (where enabled stays true
  // throughout) still re-syncs listingIds without an infinite loop from
  // setListingIds triggering a re-render that produces a new array reference.
  const mappedIds = listings.map((l) => l.id);
  const listingIdsRef = useRef(mappedIds);
  listingIdsRef.current = mappedIds;
  const listingIdsKey = listingIdsRef.current.join(',');
  const [listingIds, setListingIds] = useState<number[]>([]);
  useEffect(() => {
    if (enabled) {
      setListingIds(listingIdsRef.current);
    } else {
      setListingIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, listingIdsKey]);

  const maxVelocityCircuitBreakerStateRowVersion = useMemo(() => {
    const rowVersions = listings
      .map((listing) => listing.cirBrSuppRowVer)
      .filter((version) => version !== null && version !== undefined);

    return rowVersions.length ? Math.max(...rowVersions) : null;
  }, [listings]);

  const autoPricingSettingsUpdates = useMemo(():
    | ListingPriceCalculationsInEventInput
    | undefined => {
    if (!rawPricingSettingsUpdates) {
      return undefined;
    }
    const { rankPremium, ...rest } = rawPricingSettingsUpdates;
    // Temp solution to get pricing calculation with most updated rank premium,
    // Pricer uses the cached group pricing settings
    // Override the hasChanged to be true to passdown whatever from UI
    const result: ListingPriceCalculationsInEventInput = {
      ...rest,
      rankPremium: !rankPremium
        ? null
        : { hasChanged: true, value: rankPremium.value },
    };

    // Only include velocity circuit breaker max row version if feature is enabled
    if (hasVeloCBFeature) {
      result.velocityCircuitBreakerStateMaxRowVersion =
        maxVelocityCircuitBreakerStateRowVersion;
    }

    return result;
  }, [
    rawPricingSettingsUpdates,
    maxVelocityCircuitBreakerStateRowVersion,
    hasVeloCBFeature,
  ]);

  // Hash the current inputs so any change (listings or settings) resets the debounce.
  const inputHash = hashKey([listingIds, autoPricingSettingsUpdates]);
  const [debouncedHashKey] = useDebouncedValueSafe(inputHash);

  // Only fire when the debounced hash has caught up to the current hash, meaning
  // both listingIds and autoPricingSettingsUpdates have been stable for the full
  // debounce period. This prevents queries with new listings + stale settings when
  // switching listing groups, where listingIds updates immediately but settings
  // propagate after async form resets.
  const shouldQuery = Boolean(
    activeAccountWebClientConfig.activeAccountId &&
    event.viagId &&
    !isEmpty(listingIds) &&
    autoPricingSettingsUpdates &&
    hasAutoPricingFeature &&
    debouncedHashKey === inputHash
  );

  const activeAbortControllerRef = useRef<AbortController | null>(null);

  const listingCalculationsQuery = useQuery({
    queryKey: [
      'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
      activeAccountWebClientConfig.activeAccountId,
      event.viagId,
      debouncedHashKey,
    ],
    queryFn: async ({ signal }) => {
      if (!shouldQuery) {
        return null;
      }

      activeAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      activeAbortControllerRef.current = abortController;
      if (hasBulkPriceCalcCancellationFix) {
        signal.addEventListener('abort', () => abortController.abort());
      }

      return await trackEndpoint(
        'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
        new BulkEditListingClient(
          activeAccountWebClientConfig
        ).bulkGetListingPriceCalculationsInEvent(
          event.viagId!,
          {
            item1: listingIds,
            item2: autoPricingSettingsUpdates!,
          },
          abortController.signal
        ),
        (e: Error) => {
          abortController.abort(e.message);
        },
        5000 // 5s timeout to prevent hanging requests in case of network issues or server slowness
      );
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    // Cache results for 30s to avoid redundant API calls for identical inputs.
    // GC time matches stale time to bound memory usage, since each unique input
    // produces a large response that we don't want to retain longer than needed.
    staleTime: PricingCalculationCacheTimeInMs,
    gcTime: PricingCalculationCacheTimeInMs,
    meta: {
      onError: (error: ErrorTypes) => {
        if (
          error instanceof Error &&
          error.message === 'signal is aborted without reason'
        ) {
          return;
        }
        trackError(
          'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
          error,
          {
            trackErrorData: {
              activeAccountId: activeAccountWebClientConfig.activeAccountId,
              viagogoEventId: event.viagId,
              listingIds,
              autoPricingSettingsUpdates,
            },
          }
        );
      },
    },
  });

  return useMemo(
    () => ({
      listingCalculationsQuery,
      bulkListingPriceCalculationResult: listingCalculationsQuery.data,
    }),
    [listingCalculationsQuery]
  );
};
